import React, { useEffect, useState } from "react";
import {
  MdAdd,
  MdDelete,
  MdPeople,
  MdLogin,
  MdEdit,
} from "react-icons/md";
import toast from "react-hot-toast";
import axiosInstance from "../../utils/api";
import DeleteModal from "../../components/deleteModal";
import Sidebar from "../../components/Sidebar";
import AddUserModal from "../../components/addUserModal";

const makeEmptyForm = () => ({
  name: "",
  email: "",
  phone: "",
  permissions: [],
  eventQuota: { mode: "single", limit: 1 },
  standsQuota: { mode: "unlimited", limit: null },
});

const isUserActive = (user) => user.isActive !== false;

const StatusToggle = ({ active, loading, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    onClick={onToggle}
    disabled={loading}
    title={active ? "Deactivate user" : "Activate user"}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
      active ? "bg-gray-900" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        active ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [features, setFeatures] = useState([]);
  const [form, setForm] = useState(makeEmptyForm());

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const [usersRes, featuresRes] = await Promise.all([
        axiosInstance.get("/auth/users"),
        axiosInstance.get("/billing/pricing"),
      ]);

      setUsers(usersRes.data || []);
      setFeatures(featuresRes.data || []);
    } catch (err) {
      toast.error("Failed to load users or events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return false;
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(form.email.trim())) {
      toast.error("Please enter a valid email.");
      return false;
    }

    return true;
  };

  const featureMap = features.reduce((acc, f) => {
    acc[f.key] = f.name;
    return acc;
  }, {});

  const handleLoginAs = async (userId) => {
    try {
      const res = await axiosInstance.post(`/auth/login-as/${userId}`);
      const { code } = res.data;

      const adminUrl = new URL(process.env.REACT_APP_ADMIN_URL || "https://app.expo-pilot.com");
      adminUrl.searchParams.set("impersonate", "1");
      adminUrl.searchParams.set("code", code);

      window.open(adminUrl.toString(), "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to login as user.");
    }
  };

  const handleToggleStatus = async (user) => {
    const id = user._id || user.id;
    const nextActive = !isUserActive(user);

    try {
      setTogglingId(id);
      await axiosInstance.patch(`/auth/user/${id}/status`, { isActive: nextActive });

      setUsers((prev) =>
        prev.map((u) => ((u._id || u.id) === id ? { ...u, isActive: nextActive } : u))
      );
      toast.success(nextActive ? "User activated" : "User deactivated");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update user status.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditUser = async (user) => {
    setEditingUserId(user._id || user.id);
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      permissions: user.permissions || [],
      eventQuota: user.eventQuota || { mode: "single", limit: 1 },
      standsQuota: user.standsQuota || { mode: "unlimited", limit: null },
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      await axiosInstance.put(`/auth/user/${editingUserId}`, {
        name: form.name.trim(),
        permissions: form.permissions || [],
        eventQuota: form.eventQuota,
        standsQuota: form.standsQuota,
        ...(form.phone && { phone: form.phone.trim() }),
      });

      toast.success("User updated successfully");
      setIsEditModalOpen(false);
      setEditingUserId(null);
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const userPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        permissions: form.permissions || [],
        role: "admin",
        eventQuota: form.eventQuota || { mode: "single", limit: 1 },
        standsQuota: form.standsQuota || { mode: "unlimited", limit: null },
        ...(form.phone && { phone: form.phone.trim() }),
      };

      const userRes = await axiosInstance.post("/auth/register", userPayload);
      if (!userRes.data?.user?._id) throw new Error("User creation failed");

      toast.success("User created successfully");
      setForm(makeEmptyForm());
      setIsCreateModalOpen(false);
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.userId) return;

    try {
      setSubmitting(true);
      await axiosInstance.delete(`/auth/user/${deleteModal.userId}`);

      toast.success("User account deleted successfully.");
      setDeleteModal({ isOpen: false, userId: null });

      await fetchUsers();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete user account."
      );
      setDeleteModal({ isOpen: false, userId: null });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <MdPeople className="text-blue-900" />
              User Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Create user accounts — they'll set up and manage their own events after logging in.
            </p>
          </div>

          <button
            onClick={() => {
              setForm(makeEmptyForm());
              setIsCreateModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-sm"
          >
            <MdAdd className="w-5 h-5" />
            Create User
          </button>
        </header>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            {loading && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {users.length === 0 && !loading ? (
            <div className="py-16 text-center">
              <MdPeople className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {["User", "Contact", "Features", "Status", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {users.map((user) => {
                    const id = user._id || user.id;
                    const active = isUserActive(user);
                    const isToggling = togglingId === id;
                    const initials = (user.name || "?")
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();

                    return (
                      <tr key={id} className="hover:bg-gray-50/60 transition-colors">
                        <td className={`px-6 py-4 ${active ? "" : "opacity-50"}`}>
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
                                active ? "bg-blue-900" : "bg-gray-400"
                              }`}
                            >
                              {initials}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.name || "—"}</div>
                            </div>
                          </div>
                        </td>

                        <td className={`px-6 py-4 ${active ? "" : "opacity-50"}`}>
                          <div className="text-sm text-gray-700">{user.email || "—"}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{user.phone || "No phone"}</div>
                        </td>

                        <td className={`px-6 py-4 ${active ? "" : "opacity-50"}`}>
                          {user.permissions && user.permissions.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 max-w-xs">
                              {user.permissions.map((key) => (
                                <span
                                  key={key}
                                  className="px-2 py-0.5 text-xs font-medium text-blue-900 bg-blue-50 rounded-md border border-blue-100"
                                >
                                  {featureMap[key] || key}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300 italic">No features assigned</span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <StatusToggle
                              active={active}
                              loading={isToggling || submitting}
                              onToggle={() => handleToggleStatus(user)}
                            />
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-md border ${
                                active
                                  ? "text-gray-900 bg-gray-50 border-gray-200"
                                  : "text-red-600 bg-red-50 border-red-100"
                              }`}
                            >
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                  active ? "bg-green-500" : "bg-red-500"
                                }`}
                              />
                              {active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleLoginAs(id)}
                              disabled={submitting || !active}
                              title={active ? "Login as user" : "Activate user to login as them"}
                              className="p-2 text-gray-500 rounded-lg hover:bg-blue-50 hover:text-blue-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <MdLogin size={17} />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              disabled={submitting}
                              title="Edit"
                              className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-40"
                            >
                              <MdEdit size={17} />
                            </button>
                            <button
                              onClick={() => setDeleteModal({ isOpen: true, userId: id })}
                              disabled={submitting}
                              title="Delete"
                              className="p-2 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40"
                            >
                              <MdDelete size={17} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <AddUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        form={form}
        setForm={setForm}
        features={features}
        isSubmitting={submitting}
      />

      <AddUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateUser}
        form={form}
        setForm={setForm}
        features={features}
        isSubmitting={submitting}
        mode="edit"
      />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, userId: null })}
        onConfirm={handleDeleteUser}
        title="Delete User Account"
        message="Are you sure you want to delete this user account? This action cannot be undone."
        isDeleting={submitting}
      />
    </div>
  );
};

export default Users;